import React, { Fragment, useState } from 'react';

import { useStaticAssetPath } from '@zapier/common-components';
import { TrialConfirmationModal } from '@zapier/design-system-company';

import { Upsell } from '@zapier/design-system-revenue/src/components/offers/Upsell';
import { Size } from '@zapier/design-system-revenue/src/types';

import { FeatureData } from '../Feature';
import { FeatureList } from '../FeatureList';
import { useData as _useData } from './useData';

type CompanyUpsellProps = {
  size: Size;
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const src = {
  small: '/images/settings/company_upsell.jpg',
  medium: '/images/settings/company_upsell@2x.jpg',
  large: '/images/settings/company_upsell@3x.jpg',
};

export const CompanyUpsell = ({
  size,
  useData = _useData,
}: CompanyUpsellProps) => {
  const [isShowingCompanyTrialModal, setIsShowingCompanyTrialModal] = useState(
    false
  );
  const { accountId } = useData();
  const { buildStaticAssetUrl } = useStaticAssetPath();
  const imageSrc = buildStaticAssetUrl(src[size]);

  const features: Array<FeatureData> = [
    { iconName: 'miscFingerprint', label: 'Single Sign On' },
    { iconName: 'miscSecurity', label: 'Account Capture' },
    { iconName: 'team', label: 'Unlimited Workgroups' },
    { iconName: 'personGroup', label: 'User Provisioning' },
    { iconName: 'actionLock', label: 'App Access Control' },
    { iconName: 'arrowRefresh', label: 'Data Retention Control' },
  ];

  return (
    <Upsell
      description="Enterprise grade data controls and security features."
      imageSrc={imageSrc}
      primaryCta={{
        onClick: () => setIsShowingCompanyTrialModal(true),
        text: 'Start 14-day Trial',
      }}
      secondaryCta={{
        href: `/app/billing/plans?context=${accountId}`,
        text: 'Upgrade Now',
      }}
      size={size}
      title="Zapier for Companies"
    >
      <Fragment>
        {isShowingCompanyTrialModal && (
          <TrialConfirmationModal
            onClose={() => setIsShowingCompanyTrialModal(false)}
            size={size}
            startHref="/app/company-account-setup"
          />
        )}

        <FeatureList
          columns={size === 'large' ? 2 : 1}
          features={features}
          textType="altText900"
        />
      </Fragment>
    </Upsell>
  );
};
